// A first-person view of the town, drawn from where you stand: the ground is the town's own map laid flat and
// seen in perspective (one pixel at a time, like SNES "mode 7"), and trees, houses, lamps and people stand up
// as cut-outs facing you. Used by the sightseeing camera, and by first-person mini-games.
// Drawn small (about 320 pixels wide) and scaled up, so it stays crisp pixel art and costs little.

export interface Billboard {
  x: number; y: number;                  // feet, world pixels
  img: HTMLCanvasElement;
  ax?: number;                            // anchor: which column of the image stands on (x, y); default the middle
  scale?: number;                         // image pixels per world pixel (default 1)
  lift?: number;                          // height of the feet above the ground (world pixels)
  alpha?: number;
  name?: string; kind?: 'person' | 'prop' | 'target';
  ref?: unknown;
}
/** Where something ended up on screen in the last frame (for photo scoring and aiming). */
export interface OnScreen { b: Billboard; x: number; y: number; w: number; h: number; z: number }
export interface View { hour: number; ambient: [number, number, number]; darkness: number; rain: number; fog: number; cloudy: number; t: number;
  lights: { x: number; y: number; r: number }[] }

export class FirstPerson {
  readonly canvas = document.createElement('canvas');
  private ctx = this.canvas.getContext('2d')!;
  private layer = document.createElement('canvas');
  private lctx = this.layer.getContext('2d')!;
  private ground: Uint32Array | null = null; private gw = 0; private gh = 0;
  private img: ImageData | null = null; private buf: Uint32Array | null = null;
  W = 320; H = 180;
  cam = { x: 0, y: 0, yaw: 0, pitch: 0, fov: 62, eye: 22 };
  shown: OnScreen[] = [];

  /** The flat map under everyone's feet (ground art with decals drawn in). */
  setGround(c: HTMLCanvasElement): void {
    const d = c.getContext('2d')!.getImageData(0, 0, c.width, c.height);
    this.ground = new Uint32Array(d.data.buffer.slice(0)); this.gw = c.width; this.gh = c.height;
  }

  /** Drawing size: `width` pixels across (320 for the camera), height to match the screen's shape. */
  resize(w: number, h: number, width = 320): void {
    if (this.W === width && this.H === Math.max(150, Math.min(640, Math.round(width * h / Math.max(1, w)))) && this.img) return;
    this.W = width; this.H = Math.max(150, Math.min(640, Math.round(width * h / Math.max(1, w))));
    for (const c of [this.canvas, this.layer]) { c.width = this.W; c.height = this.H; }
    this.img = this.ctx.createImageData(this.W, this.H); this.buf = new Uint32Array(this.img.data.buffer);
    this.ctx.imageSmoothingEnabled = false; this.lctx.imageSmoothingEnabled = false;
  }

  private get focal(): number { return (this.W / 2) / Math.tan((this.cam.fov * Math.PI / 180) / 2); }
  /** Where the horizon sits: `horizonAt` of the way down, moved by looking up and down. */
  horizonAt = 0.45;
  get horizon(): number { return Math.round(this.H * this.horizonAt + this.cam.pitch); }

  /** Screen position of a world point `lift` pixels above the ground; null when behind you. */
  project(x: number, y: number, lift = 0): { sx: number; sy: number; z: number; k: number } | null {
    const c = this.cam, fx = Math.cos(c.yaw), fy = Math.sin(c.yaw), rx = -fy, ry = fx;
    const dx = x - c.x, dy = y - c.y, z = dx * fx + dy * fy; if (z < 3) return null;
    const k = this.focal / z;
    return { sx: this.W / 2 + (dx * rx + dy * ry) * k, sy: this.horizon + (c.eye - lift) * k, z, k };
  }

  render(view: View, billboards: Billboard[]): void {
    if (!this.buf || !this.img || !this.ground) return;
    const { W, H } = this, c = this.cam, f = this.focal, hz = this.horizon, buf = this.buf, g = this.ground, gw = this.gw, gh = this.gh;
    const fx = Math.cos(c.yaw), fy = Math.sin(c.yaw), rx = -fy, ry = fx;
    const [ar, ag, ab] = view.ambient, sky = skyColors(view.hour, view.rain, view.cloudy);
    // ---- sky ----
    for (let y = 0; y < Math.min(H, hz); y++) {
      const k = y / Math.max(1, hz), col = mix(sky.top, sky.bottom, k);
      const px = 0xff000000 | (col[2] << 16) | (col[1] << 8) | col[0];
      buf.fill(px, y * W, y * W + W);
    }
    // ---- ground, row by row: each row is a straight line across the map at one distance ----
    const haze = sky.bottom, maxZ = 520 - view.fog * 330, lights = view.lights.filter(L => Math.hypot(L.x - c.x, L.y - c.y) < maxZ + L.r);
    const glow = Math.min(1, view.darkness * 1.6), grass = [74, 120, 58];
    for (let y = Math.max(0, hz + 1); y < H; y++) {
      const z = c.eye * f / (y - hz), half = z * (W / 2) / f;
      let wx = c.x + fx * z - rx * half, wy = c.y + fy * z - ry * half;
      const sx = (rx * 2 * half) / W, sy = (ry * 2 * half) / W;
      const fogK = Math.min(1, Math.max(0, (z - 90) / maxZ)), row = y * W;
      // only the lights that reach this row's strip of ground
      const x1 = wx + sx * W, y1 = wy + sy * W, minX = Math.min(wx, x1), maxX = Math.max(wx, x1), minY = Math.min(wy, y1), maxY = Math.max(wy, y1);
      const rowLights = glow > 0.02 ? lights.filter(L => L.x + L.r > minX && L.x - L.r < maxX && L.y + L.r > minY && L.y - L.r < maxY) : [];
      for (let x = 0; x < W; x++, wx += sx, wy += sy) {
        let r: number, gg: number, b: number;
        const ix = wx | 0, iy = wy | 0;
        if (ix >= 0 && iy >= 0 && ix < gw && iy < gh) { const p = g[iy * gw + ix]; r = p & 255; gg = (p >> 8) & 255; b = (p >> 16) & 255; }
        else { r = grass[0]; gg = grass[1]; b = grass[2]; if (((ix >> 3) + (iy >> 3)) & 1) { r -= 6; gg -= 8; b -= 4; } }
        // light: the time of day, plus warm pools from lamps and windows at night
        let lr = ar / 255, lg = ag / 255, lb = ab / 255;
        if (rowLights.length) for (const L of rowLights) { const d = Math.hypot(wx - L.x, wy - L.y); if (d < L.r) { const a = (1 - d / L.r) ** 2 * glow * 0.9; lr += a; lg += a * 0.78; lb += a * 0.5; } }
        r = Math.min(255, r * lr); gg = Math.min(255, gg * lg); b = Math.min(255, b * lb);
        if (fogK > 0) { r += (haze[0] - r) * fogK; gg += (haze[1] - gg) * fogK; b += (haze[2] - b) * fogK; }
        buf[row + x] = 0xff000000 | ((b | 0) << 16) | ((gg | 0) << 8) | (r | 0);
      }
    }
    this.ctx.putImageData(this.img, 0, 0);
    this.drawSkyThings(view, sky);
    // ---- standing things, far to near ----
    const lc = this.lctx; lc.clearRect(0, 0, W, H);
    const list: OnScreen[] = [];
    for (const b of billboards) {
      const p = this.project(b.x, b.y, b.lift ?? 0); if (!p || p.z < 14 || p.z > maxZ + 60) continue;   // too close to see properly: step out of the way
      const ks = p.k / (b.scale ?? 1), w = b.img.width * ks, h = b.img.height * ks, x = p.sx - (b.ax ?? b.img.width / 2) * ks, top = p.sy - h;
      if (x > W || x + w < 0 || top > H || p.sy < 0) continue;
      list.push({ b, x, y: top, w, h, z: p.z });
    }
    list.sort((a, b) => b.z - a.z);
    for (const s of list) {
      lc.globalAlpha = (s.b.alpha ?? 1) * Math.min(1, (s.z - 14) / 26);   // things right in front of the lens fade away
      lc.drawImage(s.b.img, Math.round(s.x), Math.round(s.y), Math.max(1, Math.round(s.w)), Math.max(1, Math.round(s.h)));
    }
    lc.globalAlpha = 1;
    // night and weather tint on the cut-outs (keeps their outlines), then distance haze
    lc.globalCompositeOperation = 'source-atop';
    if (view.darkness > 0.02) { lc.fillStyle = `rgba(${ar >> 2},${ag >> 2},${Math.min(255, (ab >> 1) + 20)},${Math.min(0.72, view.darkness * 1.1)})`; lc.fillRect(0, 0, W, H); }
    if (view.fog > 0.05) { lc.fillStyle = `rgba(${haze[0]},${haze[1]},${haze[2]},${0.35 * view.fog})`; lc.fillRect(0, 0, W, H); }
    lc.globalCompositeOperation = 'source-over';
    this.ctx.drawImage(this.layer, 0, 0);
    this.shown = list;
    // ---- rain ----
    if (view.rain > 0.05) {
      this.ctx.fillStyle = 'rgba(200,215,255,0.5)';
      const n = Math.round(view.rain * 140);
      for (let i = 0; i < n; i++) { const x = (i * 97 + Math.floor(view.t * 400) * (i % 3 + 1)) % W, y = (i * 53 + Math.floor(view.t * 900)) % H; this.ctx.fillRect(x, y, 1, 4); }
    }
  }

  private drawSkyThings(view: View, sky: { top: number[]; bottom: number[] }): void {
    const { W, H } = this, ctx = this.ctx, hz = this.horizon, c = this.cam, f = this.focal;
    const az = (a: number) => { let d = a - c.yaw; while (d > Math.PI) d -= 2 * Math.PI; while (d < -Math.PI) d += 2 * Math.PI; return d; };
    const screenX = (a: number) => W / 2 + Math.tan(az(a)) * f;
    const night = view.hour >= 20 || view.hour < 5.5;
    // stars
    if (night && view.cloudy < 0.6) {
      ctx.fillStyle = '#fff3c4';
      for (let i = 0; i < 90; i++) {
        const a = (i * 2.399) % (Math.PI * 2), el = 0.12 + ((i * 0.618) % 1) * 0.85, d = az(a);
        if (Math.abs(d) > 1.2) continue;
        const x = screenX(a), y = hz - el * hz; if (Math.sin(view.t * 1.3 + i) > 0.92) continue;
        ctx.fillRect(Math.round(x), Math.round(y), 1, 1);
      }
    }
    // the sun (rises in the east, south at noon, sets in the west) and the moon on the other side
    const sunA = ((view.hour - 6) / 12) * Math.PI, sunEl = Math.sin(sunA);
    for (const [a, el, col, r] of [[sunA, sunEl, view.hour > 17.5 || view.hour < 7.5 ? '#ffb070' : '#fff3c4', 9], [sunA + Math.PI, -sunEl, '#e8eef8', 6]] as [number, number, string, number][]) {
      if (el < -0.05 || Math.abs(az(a)) > 1.1 || view.cloudy > 0.7) continue;
      const x = screenX(a), y = hz - el * (hz - 10) - 4;
      const gr = ctx.createRadialGradient(x, y, 0, x, y, r * 3); gr.addColorStop(0, col); gr.addColorStop(0.35, col + 'aa'); gr.addColorStop(1, col + '00');
      ctx.fillStyle = gr; ctx.fillRect(x - r * 3, y - r * 3, r * 6, r * 6);
      ctx.fillStyle = col; ctx.beginPath(); ctx.arc(x, y, r * 0.6, 0, Math.PI * 2); ctx.fill();
    }
    // far hills all around the town
    const hill = mix(sky.bottom, [60, 96, 70], night ? 0.45 : 0.6);
    ctx.fillStyle = `rgb(${hill[0]},${hill[1]},${hill[2]})`;
    for (let x = 0; x < W; x += 2) {
      const a = c.yaw + Math.atan((x - W / 2) / f), hh = 7 + 4 * Math.sin(a * 3) + 3 * Math.sin(a * 7 + 1) + 2 * Math.sin(a * 13 + 2);
      ctx.fillRect(x, hz - hh, 2, hh + 1);
    }
    // clouds
    if (view.cloudy > 0.05 || !night) {
      ctx.fillStyle = night ? `rgba(70,80,120,${0.4 * Math.max(0.3, view.cloudy)})` : `rgba(255,255,255,${0.55 * Math.max(0.35, view.cloudy)})`;
      for (let i = 0; i < 7; i++) {
        const a = i * 0.9 + view.t * 0.004, d = az(a); if (Math.abs(d) > 1.2) continue;
        const x = screenX(a), y = hz * (0.25 + (i % 3) * 0.17);
        ctx.beginPath(); ctx.ellipse(x, y, 26 + (i % 2) * 14, 6 + (i % 3) * 2, 0, 0, Math.PI * 2); ctx.fill();
        ctx.beginPath(); ctx.ellipse(x + 14, y - 4, 16, 6, 0, 0, Math.PI * 2); ctx.fill();
      }
    }
    void H;
  }
}

const mix = (a: number[], b: number[], k: number) => a.map((v, i) => Math.round(v + (b[i] - v) * k));
function skyColors(hour: number, rain: number, cloudy: number): { top: number[]; bottom: number[] } {
  const keys: [number, number[], number[]][] = [
    [0, [12, 18, 46], [36, 46, 92]], [5, [30, 32, 76], [120, 90, 120]], [6.3, [96, 140, 210], [250, 190, 160]], [8, [96, 170, 235], [196, 230, 250]],
    [17, [96, 170, 235], [196, 230, 250]], [18.8, [90, 110, 190], [250, 160, 120]], [20, [40, 44, 100], [140, 90, 120]], [21.5, [12, 18, 46], [36, 46, 92]], [24, [12, 18, 46], [36, 46, 92]],
  ];
  let i = 0; while (i < keys.length - 2 && keys[i + 1][0] <= hour) i++;
  const k = (hour - keys[i][0]) / (keys[i + 1][0] - keys[i][0] || 1);
  let top = mix(keys[i][1], keys[i + 1][1], k), bottom = mix(keys[i][2], keys[i + 1][2], k);
  const grey = Math.min(1, rain * 0.8 + cloudy * 0.45);
  if (grey > 0) { const gt = top.reduce((a, b) => a + b) / 3, gb = bottom.reduce((a, b) => a + b) / 3; top = mix(top, [gt, gt, gt + 6], grey); bottom = mix(bottom, [gb, gb, gb + 6], grey); }
  return { top, bottom };
}
